// Proposition TRAME — mise en forme PARTAGÉE entre l'écran (web/src/pages/conception/proposition) et le PDF du plan annoté
// (server/modules/conception/proposition-pdf.ts) : mêmes teintes de pièces, mêmes couleurs et motifs de diamètres, mêmes textes
// « besoin / installé — % », même numérotation et même position des points à traiter. Fonctions pures, sans aucune règle de calcul :
// le moteur et le serveur font foi.
import { barycentre, distance } from './geometrie.ts';
import type { AffectationPiece, BesoinPiece, PointControle, Pt, TronconReseau, VarianteProposition } from './types.ts';

/** Calques du plan annoté (§ 8.1) : mêmes clés et libellés à l'écran et dans le PDF. */
export const CALQUES_PROPOSITION = [
  ['pieces', 'Pièces'], ['unites', 'Unités intérieures'], ['ue', 'Groupes extérieurs'], ['reseau', 'Réseau frigorifique'],
  ['derivations', 'Dérivations et passages'], ['condensats', 'Condensats'], ['bus', 'Bus et alimentations'], ['points', 'Points à traiter'],
] as const;
export type CalqueProposition = typeof CALQUES_PROPOSITION[number][0];

/* ─────────────────────────────── formats ─────────────────────────────── */

const nf = (d: number) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d });
/** Puissance en kW (1 décimale) ; null = « à saisir » (jamais 0). */
export const kw = (w: number | null | undefined) => (w == null ? 'à saisir' : `${nf(1).format(w / 1000)} kW`);
/** Rapport → pourcentage entier (1,17 → « 117 % »). */
export const pct = (r: number | null | undefined) => (r == null ? '—' : `${nf(0).format(Math.round(r * 100))} %`);
export const metres = (m: number | null | undefined, d = 1) => (m == null ? 'à calculer' : `${nf(d).format(m)} m`);
export const nombre = (n: number | null | undefined, d = 0) => (n == null ? '—' : nf(d).format(n));

export const centre = (poly: Pt[]): Pt => (poly.length >= 3 ? barycentre(poly) : poly.length ? poly[0] : [0, 0]);

/* ─────────────────────────────── pièces ─────────────────────────────── */

export type TonPiece = 'non_traitee' | 'incomplet' | 'sans_unite' | 'non_couvert' | 'couvert';
export interface EtatPiece { ton: TonPiece; texte: string; couverture: number | null }
/**
 * Teinte et texte d'une pièce : « 7,7 kW besoin / 9,0 kW installés — 117 % ». La couverture retenue est la plus faible du froid et
 * du sensible (le moteur contrôle les deux) ; le chaud est affiché à part dans la fiche.
 */
export function etatPiece(b: BesoinPiece | undefined, a: AffectationPiece | undefined): EtatPiece {
  if (!b || !b.traitee) return { ton: 'non_traitee', texte: 'Non traitée', couverture: null };
  if (b.froidW == null || b.incomplet.length) return { ton: 'incomplet', texte: `Besoin incomplet${b.incomplet.length ? ` : ${b.incomplet.join(', ')}` : ''}`, couverture: null };
  if (!a || !a.nombre || a.installeFroidW == null) return { ton: 'sans_unite', texte: `${kw(b.froidW)} besoin / aucune unité`, couverture: 0 };
  const c = [a.couverture.froid, a.couverture.sensible].filter((x): x is number => x != null);
  const couverture = c.length ? Math.min(...c) : null;
  const texte = `${kw(b.froidW)} besoin / ${kw(a.installeFroidW)} installés — ${pct(a.couverture.froid)}`;
  return { ton: couverture != null && couverture >= 1 ? 'couvert' : 'non_couvert', texte: couverture != null && couverture < 1 && (a.couverture.froid ?? 0) >= 1 ? `${texte} (sensible ${pct(a.couverture.sensible)})` : texte, couverture };
}
export const LIBELLE_TON: Record<TonPiece, string> = {
  couvert: 'Besoin couvert', non_couvert: 'Besoin non couvert', sans_unite: 'Aucune unité', incomplet: 'Besoin incomplet', non_traitee: 'Non traitée',
};
/** Teintes des pièces (fond, trait). Couleurs fixes : le plan est un papier blanc dans les deux thèmes, et à l'impression. */
export const TEINTES_PIECE: Record<TonPiece, { fond: string; trait: string }> = {
  couvert: { fond: 'rgba(29,107,65,0.16)', trait: '#1D6B41' }, non_couvert: { fond: 'rgba(180,35,24,0.16)', trait: '#B42318' },
  sans_unite: { fond: 'rgba(180,35,24,0.10)', trait: '#B42318' }, incomplet: { fond: 'url(#prp-hachures)', trait: '#8A4B00' },
  non_traitee: { fond: 'rgba(91,102,118,0.14)', trait: '#5B6676' },
};

/* ─────────────────────────────── réseau (couleur + motif + étiquette) ─────────────────────────────── */

/** Couleurs fixes lisibles sur le papier blanc du plan (≥ 3:1 sur #FFFFFF), dans les deux thèmes. */
export const COULEURS_DIAMETRE = ['#1F5FBF', '#B05500', '#2E7D32', '#A23B72', '#5B3FBF', '#00656A', '#7A5B00', '#3F5F7F'];
/** Motifs de trait (longueurs en pixels d'écran ou en points PDF), pour ne pas dépendre de la couleur seule. */
export const MOTIFS_DIAMETRE = ['', '14 5', '4 4', '16 4 3 4', '8 8', '2 6', '22 6', '10 3 3 3'];
export interface StyleDiametre { cle: string; couleur: string; motif: string; libelle: string; libelleMm: string; ordre: number }

const mm = (d: { mm: string } | null) => (d ? Number(d.mm) : 0);
export const cleDiametre = (t: Pick<TronconReseau, 'liquide' | 'gaz'>) => `${t.liquide?.pouces ?? '?'}|${t.gaz?.pouces ?? '?'}`;
const libelleDiametre = (t: Pick<TronconReseau, 'liquide' | 'gaz'>) => `L ${t.liquide ? `${t.liquide.pouces}″` : 'à relever'} · G ${t.gaz ? `${t.gaz.pouces}″` : 'à relever'}`;
const libelleMm = (t: Pick<TronconReseau, 'liquide' | 'gaz'>) => `${t.liquide ? nf(2).format(Number(t.liquide.mm)) : '?'} / ${t.gaz ? nf(2).format(Number(t.gaz.mm)) : '?'} mm`;

/** Un style par couple liquide / gaz présent, du plus petit au plus gros (gaz puis liquide) : déterministe. */
export function stylesDiametres(troncons: Pick<TronconReseau, 'liquide' | 'gaz'>[]): Map<string, StyleDiametre> {
  const uniques = new Map<string, Pick<TronconReseau, 'liquide' | 'gaz'>>();
  for (const t of troncons) if (!uniques.has(cleDiametre(t))) uniques.set(cleDiametre(t), t);
  const tri = [...uniques.entries()].sort(([, a], [, b]) => mm(a.gaz) - mm(b.gaz) || mm(a.liquide) - mm(b.liquide));
  return new Map(tri.map(([cle, t], i) => [cle, { cle, couleur: COULEURS_DIAMETRE[i % COULEURS_DIAMETRE.length], motif: MOTIFS_DIAMETRE[i % MOTIFS_DIAMETRE.length],
    libelle: libelleDiametre(t), libelleMm: libelleMm(t), ordre: i }]));
}

/** Point d'ancrage de l'étiquette d'un tracé : milieu du plus long segment. */
export function ancrageTrace(trace: Pt[]): Pt | null {
  if (!trace.length) return null;
  if (trace.length === 1) return trace[0];
  let best = 0, iBest = 0;
  for (let i = 0; i < trace.length - 1; i++) { const d = distance(trace[i], trace[i + 1]); if (d > best) { best = d; iBest = i; } }
  return [(trace[iBest][0] + trace[iBest + 1][0]) / 2, (trace[iBest][1] + trace[iBest + 1][1]) / 2];
}

/* ─────────────────────────────── points de contrôle ─────────────────────────────── */

export interface PointLocalise extends PointControle { numero: number; ou: { planId: string; x: number; y: number } | null }
/**
 * Position d'un point sur le plan : sa position si le moteur l'a donnée, sinon celle de sa cible (pièce, unité, tronçon, système,
 * groupe extérieur, condensat, liaison du technicien). null = point général (il reste listé, sans centrage).
 */
export function positionPoint(p: PointControle, v: VarianteProposition, pieces: Map<string, { planId: string; polygone: Pt[] }>): { planId: string; x: number; y: number } | null {
  if (p.position) return p.position;
  const c = p.cible;
  if (!c) return null;
  const surTrace = (planId: string, trace: Pt[]) => { const a = ancrageTrace(trace); return a ? { planId, x: a[0], y: a[1] } : null; };
  switch (c.type) {
    case 'piece': { const pc = pieces.get(c.id); if (!pc) return null; const [x, y] = centre(pc.polygone); return { planId: pc.planId, x, y }; }
    case 'unite': { const u = v.unites.find((x) => x.id === c.id); return u ? { planId: u.planId, x: u.x, y: u.y } : null; }
    case 'ue': { const g = v.groupesExterieurs.find((x) => x.id === c.id || x.systemeId === c.id); return g ? { planId: g.planId, x: g.x, y: g.y } : null; }
    case 'systeme': { const g = v.groupesExterieurs.find((x) => x.systemeId === c.id); return g ? { planId: g.planId, x: g.x, y: g.y } : null; }
    case 'troncon': case 'liaison': {
      const t = v.troncons.find((x) => x.id === c.id || x.id === `technicien-${c.id}`);
      return t ? surTrace(t.planId, t.trace) : null;
    }
    case 'condensat': { const k = v.condensats.find((x) => x.id === c.id || x.uniteId === c.id); return k ? surTrace(k.planId, k.trace) : null; }
    default: return null;
  }
}
/** Points bloquants puis « à vérifier », numérotés dans cet ordre (le numéro est celui de la pastille sur le plan). */
export function pointsLocalises(v: VarianteProposition, pieces: Map<string, { planId: string; polygone: Pt[] }>): PointLocalise[] {
  const ordre = { bloquant: 0, a_verifier: 1, ok: 2 };
  return v.points.filter((p) => p.niveau !== 'ok').map((p, i) => ({ p, i })).sort((a, b) => ordre[a.p.niveau] - ordre[b.p.niveau] || a.i - b.i)
    .map(({ p }, i) => ({ ...p, numero: i + 1, ou: positionPoint(p, v, pieces) }));
}
/** Décalage des pastilles superposées (même endroit) : en ligne, pas de 1. */
export function decalages(points: { id: string; ou: { x: number; y: number } | null }[], tolerance: number): Map<string, number> {
  const groupes = new Map<string, number>(); const r = new Map<string, number>();
  for (const p of points) {
    if (!p.ou) continue;
    const cle = `${Math.round(p.ou.x / tolerance)}:${Math.round(p.ou.y / tolerance)}`;
    const n = groupes.get(cle) ?? 0; groupes.set(cle, n + 1); r.set(p.id, n);
  }
  return r;
}

/* ─────────────────────────────── comparaison ─────────────────────────────── */

/** Montant HT (chaîne décimale du serveur) — à n'appeler QUE pour un compte ayant le droit devis.couts. */
export const montantHt = (s: string) => `${nf(2).format(Number(s))} € HT`;
export const contientMontant = (texte: string) => /€|\bEUR\b/.test(texte);

/** Valeur affichée d'un critère du tableau comparatif. Le coût n'est jamais rendu sans le droit devis.couts. */
export function valeurCritere(cle: string, v: unknown, couts: boolean): string {
  if (cle === 'cout') {
    if (!couts || v === 'réservé') return 'réservé';
    if (v === 'coût incomplet' || v == null) return 'coût incomplet';
    return montantHt(String(v));
  }
  if (v == null) return '—';
  if (cle === 'couverture') return pct(Number(v));
  if (cle === 'surpuissance') return `${nombre(Number(v), 0)} %`;
  if (cle === 'cuivre_reel' || cle === 'cuivre_equivalent' || cle === 'condensats') return metres(Number(v));
  if (cle === 'charge') return `${nombre(Number(v), 0)} g`;
  if (cle === 'acoustique') return `${nombre(Number(v), 1)} dB`;
  return typeof v === 'number' ? nombre(v) : String(v);
}
/** Le plus petit est-il le meilleur pour ce critère ? (pour marquer la meilleure valeur, sans pondération). */
export const MEILLEUR_PETIT = new Set(['bloquants', 'a_verifier', 'surpuissance', 'unites', 'cuivre_reel', 'cuivre_equivalent', 'derivations', 'traversees', 'condensats', 'pompes', 'charge']);
export function meilleurs(cle: string, valeurs: unknown[]): Set<number> {
  const n = valeurs.map((v) => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : null));
  const ok = n.filter((x): x is number => x != null);
  if (ok.length < 2 || cle === 'cout') return new Set();
  const cible = MEILLEUR_PETIT.has(cle) ? Math.min(...ok) : cle === 'couverture' || cle === 'acoustique' ? Math.max(...ok) : null;
  if (cible == null || ok.every((x) => x === cible)) return new Set();
  return new Set(n.flatMap((x, i) => (x === cible ? [i] : [])));
}

/** Couverture par pièce traitée, pour chaque variante (ligne du comparatif « besoin couvert par pièce »). */
export function couverturesParPiece(variantes: (VarianteProposition | undefined)[]): { pieceId: string; nom: string; valeurs: (EtatPiece | null)[] }[] {
  const ref = variantes.find(Boolean);
  if (!ref) return [];
  return ref.besoins.filter((b) => b.traitee).map((b) => ({
    pieceId: b.pieceId, nom: b.nom,
    valeurs: variantes.map((v) => (v ? etatPiece(v.besoins.find((x) => x.pieceId === b.pieceId), v.affectations.find((x) => x.pieceId === b.pieceId)) : null)),
  }));
}
